import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Img, Text, Link, useStyleConfig, AspectRatio} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import useDamImage from '../../../custom-hooks/use-dam-image'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../utils/contentstack-utils'
import SanitizedHtml from '../../html-sanitizer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

export const PLPBannerPromo = ({heading, bodycopy, image, button, maxWidth = remCalc(1440)}) => {
    const styles = useStyleConfig('PlpBannerPromo')
    const damImage = useDamImage(image)
    const categoryData = useGetCategoriesData()
    const {url, label, isAbsolute} = getGlobalLink(button, categoryData)
    const {buildUrl} = useMultiSite()
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    return (
        <Box
            {...styles.box}
            data-testid="plp-banner-promo-container"
            width="100%"
            bg={styles.box?.bg}
        >
            <Flex
                maxW={maxWidth}
                m="0 auto"
                w="100%"
                position="relative"
                direction={{base: 'column-reverse', md: 'row'}}
                px={{base: remCalc(16), md: remCalc(24)}}
                py={{base: remCalc(40), md: remCalc(24)}}
                gap={{base: remCalc(16), sm: remCalc(24)}}
            >
                <Box data-testid="plp-banner-promo-image" {...styles.boximage}>
                    {(damImage.url || image?.alt) && (
                        <AspectRatio ratio={damImage.ratio}>
                            <Img
                                data-testid="plp-banner-promo-image"
                                src={damImage.url}
                                alt={image?.alt || ''}
                                {...styles.image}
                            />
                        </AspectRatio>
                    )}
                </Box>
                <Box {...styles.textblock}>
                    {heading && (
                        <Text
                            data-testid="plp-banner-promo-heading"
                            textStyle="head-large"
                            fontWeight="medium"
                            color="text.inverse"
                            pb={{base: remCalc(8), md: remCalc(16), xl: remCalc(24)}}
                        >
                            {heading}
                        </Text>
                    )}
                    {bodycopy && (
                        <SanitizedHtml
                            as={Text}
                            html={bodycopy}
                            data-testid="plp-banner-promo-bodycopy"
                            textStyle="body-medium"
                            fontWeight="normal"
                            color="text.inverse"
                            pb={{base: remCalc(8), md: remCalc(16), xl: remCalc(24)}}
                        />
                    )}
                    {label && url && (
                        <Button
                            data-testid="plp-banner-promo-button"
                            {...buttonProps}
                            minW={{base: remCalc(227), md: remCalc(310), lg: remCalc(213)}}
                            variant="inverse-primary"
                        >
                            {label}
                        </Button>
                    )}
                </Box>
            </Flex>
        </Box>
    )
}

PLPBannerPromo.propTypes = {
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    image: PropTypes.shape(),
    button: PropTypes.shape(),
    textAlignment: PropTypes.oneOf([
        'top-center',
        'middle-right',
        'bottom-center',
        'middle-left',
        'middle-center',
    ]),
    variant: PropTypes.string,
    height: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
}
